import { useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Map } from './components/Map';
import Sidebar from './components/Sidebar';
import RightRail from './components/RightRail';
import { useAppStore, ExplorerScope } from './store';
import { useUrlSync } from './useUrlSync';
import './explorer.css';

interface ZarrExplorerProps {
  scope?: ExplorerScope;
  datasetId?: string;
  /** If true, skip the outer Layout (for embedding inside a collection tab). */
  embedded?: boolean;
}

const ZarrExplorer = ({ scope = 'global', datasetId, embedded = false }: ZarrExplorerProps) => {
  const [searchParams] = useSearchParams();
  const setDatasetId = useAppStore(s => s.setDatasetId);
  const setScope = useAppStore(s => s.setScope);

  // useLayoutEffect so the store's scope is set before the first paint —
  // useEffect fires after paint, leaving scope='global' on the first frame
  // and hiding the collection-mode UI (e.g. the "Collection metadata" link).
  useLayoutEffect(() => {
    if (scope === 'collection' && datasetId) {
      setScope('collection', datasetId);
      setDatasetId(datasetId);
    } else {
      setScope('global', null);
      // Back-compat: honor legacy ?dataset=. useUrlSync handles the new
      // ?collection= param and the rest of the state hydration.
      const dataset = searchParams.get('dataset');
      if (dataset) setDatasetId(dataset);
    }
    return () => {
      // Reset scope on unmount so re-navigating to /zarr-explorer starts global.
      setScope('global', null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, datasetId]);

  // Round-trip the viewer state to the URL only for the standalone route.
  // The collection-tab embed doesn't own the page URL.
  useUrlSync(scope !== 'collection' && !embedded);

  const inner = (
    <div style={{ display: 'flex', height: '100%', position: 'relative' }}>
      <Sidebar />
      <Map />
      <RightRail collectionMode={scope === 'collection'} />
    </div>
  );

  if (embedded) {
    return <div style={{ height: '100%', position: 'relative' }}>{inner}</div>;
  }

  return (
    <Layout
      onGrid={false}
      allowAnnouncement={false}
      mainStyle={{
        height: 'calc(100vh - 5.5rem - 27px)',
        overflow: 'hidden',
        padding: 0,
        position: 'relative',
      }}
    >
      {inner}
    </Layout>
  );
};

export default ZarrExplorer;
